import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreditCard, ChevronDown, ChevronRight, Upload, EyeOff, AlertTriangle } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : "–");

export function MissingReceiptsPanel({ companyId, onUploadFor, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(90);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/cards/missing-receipts`, { params: { days } });
      setData(r.data);
    } catch { setData(null); }
  }, [companyId, days]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const dismiss = async (it) => {
    try {
      await apiClient.post(`/companies/${companyId}/cards/missing-receipts/${it.journal_entry_id}/dismiss`);
      setData((d) => d && { ...d, items: d.items.filter((x) => x.journal_entry_id !== it.journal_entry_id), count: d.count - 1, total: Math.round((d.total - it.amount) * 100) / 100 });
    } catch { toast.error("Konnte nicht ausgeblendet werden"); }
  };

  if (!data || data.count === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70" data-testid="missing-receipts-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left" data-testid="missing-receipts-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-amber-700" /> : <ChevronRight className="h-4 w-4 text-amber-700" />}
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-medium text-amber-900" data-testid="missing-receipts-summary">
          Beleg fehlt: {data.count} Kartenzahlung(en) ohne Quittung · CHF {fmt(data.total)}
        </span>
        <span className="ml-auto text-xs text-amber-800">letzte {data.days} Tage</span>
      </button>
      {open && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 text-xs text-amber-800 mb-2">
            <span>Zahlungen ab Firmenkonto, zu denen im Bankabgleich noch kein Beleg zugeordnet ist.</span>
            <select className="ml-auto h-7 rounded border border-amber-300 bg-white px-1 text-xs" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="missing-receipts-days">
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
              <option value={180}>180 Tage</option>
              <option value={365}>1 Jahr</option>
            </select>
          </div>
          <ul className="divide-y divide-amber-100 rounded border border-amber-200 bg-white max-h-80 overflow-y-auto">
            {data.items.map((it) => (
              <li key={it.journal_entry_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`missing-receipt-${it.journal_entry_id}`}>
                <span className="text-xs text-slate-500 w-20 shrink-0">{fmtDate(it.purchase_date)}</span>
                <span className="flex-1 truncate text-slate-900" title={it.merchant}>{it.merchant}</span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-600 shrink-0">
                  <CreditCard className="h-3 w-3" />{it.last4 ? `…${it.last4}` : "Karte"}{it.card_label ? ` · ${it.card_label}` : ""}
                </span>
                <span className="font-medium tabular-nums w-24 text-right shrink-0">CHF {fmt(it.amount)}</span>
                <Button size="sm" variant="outline" className="h-7" onClick={() => onUploadFor?.(it)} data-testid={`missing-receipt-upload-${it.journal_entry_id}`}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Beleg hochladen
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-slate-500" onClick={() => dismiss(it)} title="Kein Beleg nötig / ausblenden" data-testid={`missing-receipt-dismiss-${it.journal_entry_id}`}>
                  <EyeOff className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
